/**
 * Add Network Site Form Schema
 *
 * Complex schema for creating network sites with conditional fields,
 * repeating network technology sub-fields, and file uploads.
 * Form metadata (name, title, description) is loaded from form.yml
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'
import {
  BACKHAUL_OPTIONS,
  type BackhaulCapability,
  type NetworkTechnologyType,
} from '@/types/sensNet'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Helper Schemas
// ============================================================================

const GPSLocationSchema = z.object({
  latitude: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number({ message: 'Latitude is required' }).refine((val) => !Number.isNaN(val), { message: 'Latitude must be a valid number' })
  ),
  longitude: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number({ message: 'Longitude is required' }).refine((val) => !Number.isNaN(val), { message: 'Longitude must be a valid number' })
  ),
})

const NetworkTechnologySchema = z.object({
  technology: z.enum(['4G', '5G', 'LoRa', 'Wifi', 'RTK'] as [NetworkTechnologyType, ...NetworkTechnologyType[]]),
  spectrum: z.string().optional().default(''),
  band: z.string().optional().default(''),
  frequencyChannel: z.string().optional().default(''),
  numberOfSectors: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number().int().positive().optional()
  ),
  antennaType: z.string().optional().default(''),
})

const BackhaulCapabilityEnum = z.enum(Object.values(BACKHAUL_OPTIONS) as [BackhaulCapability, ...BackhaulCapability[]])

// ============================================================================
// Form Schema (What user fills in)
// ============================================================================

export const AddNetworkSiteFormSchema = z
  .object({
    networkSiteNumber: z.number().optional(), // Business key for edit mode

    corridorId: z
      .string()
      .min(1, 'Network corridor is required'),

    name: z
      .string()
      .min(1, 'Name is required')
      .max(200, 'Name cannot exceed 200 characters'),

    siteAddress: z
      .string()
      .min(1, 'Site address is required'),

    siteGeoLocation: GPSLocationSchema,

    locationNotes: z.string().optional(),

    // Optional per backend (indoorOrOutdoorSite is nullish). Empty select value ('')
    // is treated as "not set" so it passes optional validation.
    indoorOrOutdoorSite: z.preprocess(
      (v) => (v === '' || v == null ? undefined : v),
      z.enum(['indoor', 'outdoor']).optional(),
    ),

    verticalStructureHeight: z.preprocess(
      (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
      z.number().positive('Vertical structure height must be positive').refine((val) => !Number.isNaN(val), { message: 'Must be a valid number' }).optional()
    ),

    outdoorVerticalStructureType: z
      .enum(['Steel Monopole', 'Lattice', 'Guyed', 'Wooden Monopole'])
      .optional(),

    indoorStructureType: z
      .enum(['building', 'cattleShed', 'barn', 'greenhouse'])
      .optional(),

    siteOwnership: z.string().optional(),

    // Technologies selected in the multi-select (used to drive repeating sub-fields)
    selectedTechnologies: z.array(z.enum(['4G', '5G', 'LoRa', 'Wifi', 'RTK'])).optional().default([]),

    // Full technology details (one entry per selected technology)
    networkTechnologies: z.array(NetworkTechnologySchema).optional().default([]),

    // Optional per backend (backhaulCapability is nullish). Empty select value ('')
    // is treated as "not set" — this was silently blocking Add (enum rejects '').
    backhaulCapability: z.preprocess(
      (v) => (v === '' || v == null ? undefined : v),
      BackhaulCapabilityEnum.optional(),
    ),

    shelterAvailable: z.boolean().optional().default(false),

    cabinetAttached: z.boolean().optional().default(false),

    sitePictures: z
      .array(z.any())
      .max(10, 'You can upload up to 10 pictures')
      .optional()
      .default([]),

    // Stored as ISO string in the form; transformed to timestamp for API
    networkSiteLaunchDate: z.string().optional(),
  })
  .superRefine((value, ctx) => {
    // Structure fields (site type, vertical height, outdoor/indoor structure type) are
    // all optional per the backend (nullish) — no conditional requirement is enforced.

    // Note: Detailed networkTechnologies validation is optional because:
    // 1. The backend API only accepts networkSiteTechnologies (simple string array from selectedTechnologies)
    // 2. The detailed networkTechnologies array is collected for future use but not currently required by the API
    // If you want to require detailed technology info in the future, uncomment this validation:
    // if ((value.selectedTechnologies?.length ?? 0) > 0 && (value.networkTechnologies?.length ?? 0) === 0) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     path: ['networkTechnologies'],
    //     message: 'Please provide details for each selected technology',
    //   })
    // }

    // Validate file sizes if running in a browser environment
    if (Array.isArray(value.sitePictures) && value.sitePictures.length > 0) {
      const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024 // 5MB
      const MAX_TOTAL_SIZE_BYTES = 25 * 1024 * 1024 // 25MB

      let totalSize = 0
      value.sitePictures.forEach((file: any, index: number) => {
        if (file && typeof file === 'object' && 'size' in file) {
          const fileSize = (file as File).size
          totalSize += fileSize
          if (fileSize > MAX_FILE_SIZE_BYTES) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['sitePictures', index],
              message: 'Each file must be 5MB or smaller',
            })
          }
        }
      })

      if (totalSize > MAX_TOTAL_SIZE_BYTES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sitePictures'],
          message: 'Total file size must not exceed 25MB',
        })
      }
    }
  })

export type AddNetworkSiteFormData = z.infer<typeof AddNetworkSiteFormSchema>

// ============================================================================
// API Transformation
// ============================================================================

/**
 * Transform form data to API request format.
 * Maps the flat form structure to the NetworkSite API type.
 *
 * Note: The backend API currently only accepts:
 * - networkSiteName (required)
 * - networkSiteAddress (required)
 * - location (required, with latitude/longitude)
 * - networkSiteTechnologies (optional, array of strings: "Wifi", "LoRa", "4G", "5G")
 * - activationDate (optional, epoch seconds)
 * - id, networkSiteNumber, archiveDate (optional, for updates)
 *
 * Additional fields collected in the form (locationNotes, indoorOrOutdoorSite,
 * verticalStructureHeight, networkTechnologies details, etc.) are not yet supported
 * by the API endpoint.
 */
export function transformToApiRequest(formData: AddNetworkSiteFormData) {
  // Map form location to API location format
  const location = {
    latitude: formData.siteGeoLocation.latitude,
    longitude: formData.siteGeoLocation.longitude,
  }

  // Convert detailed networkTechnologies array to simple string array for API
  // The API expects: networkSiteTechnologies: ["4G", "5G", "Wifi", "LoRa"]
  const networkSiteTechnologies: string[] = (formData.selectedTechnologies || []).map(tech => tech)

  return {
    networkSiteNumber: formData.networkSiteNumber, // Business key for upsert
    networkSiteName: formData.name.trim(),
    networkSiteAddress: formData.siteAddress.trim(),
    location,
    networkSiteTechnologies,
    networkCorridorNumber: (formData as any).corridorId ? parseInt((formData as any).corridorId, 10) : undefined,
    backhaulCapability: formData.backhaulCapability,
    shelterAvailable: formData.shelterAvailable ?? false,
    cabinetAttached: formData.cabinetAttached ?? false,
    // Extract the actual URL string from each item.
    // onSubmit may wrap plain strings into { objectKey, fileName, ... } objects,
    // so we unwrap before filtering.
    ...((): { sitePictures: string[]; _allSitePictures: string[] } => {
      const allSitePictures = (formData.sitePictures || []).map((item: any) => {
        if (typeof item === 'string') return item
        if (item && typeof item === 'object') return String(item.objectKey ?? item.url ?? item.path ?? '')
        return ''
      }).filter(Boolean)
      // Strip data: URLs — dev-mode previews must never be sent to the backend.
      const sitePictures = allSitePictures.filter((v) => !v.startsWith('data:'))
      return { sitePictures, _allSitePictures: allSitePictures }
    })(),
    activationDate: formData.networkSiteLaunchDate
      ? Math.floor(new Date(formData.networkSiteLaunchDate).getTime() / 1000)
      : undefined,
    locationNotes: formData.locationNotes,
    siteOwnership: formData.siteOwnership,
    indoorOrOutdoorSite: formData.indoorOrOutdoorSite,
    verticalStructureHeight: formData.verticalStructureHeight,
    outdoorVerticalStructureType: formData.outdoorVerticalStructureType,
    indoorStructureType: formData.indoorStructureType,
  }
}
